import { Component, inject, signal, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';

import { Types } from '../../../domain/types.domain';
import { AppHandler } from '../../app.handler';
import { AuthRepositoryInstruction } from '../../../builder/instructions/repositories/auth.repository.instructions';
import { ValidateAccountConfirmationServiceInstruction } from '../../../builder/instructions/services/validate-account-confirmation.service.instruction';
import { IconComponent, LoadingBarComponent } from '../../../@ads/components';

@Component({
  selector: 'app-account-confirmation',
  standalone: true,
  imports: [CommonModule, IconComponent, LoadingBarComponent],
  providers: [
    new AuthRepositoryInstruction(),
    new ValidateAccountConfirmationServiceInstruction(),
  ],
  templateUrl: './account-confirmation.component.html',
  styleUrl: './account-confirmation.component.scss',
})
export class AccountConfirmationComponent implements OnInit {
  private readonly _route = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _appHandler = inject(AppHandler);
  private readonly _validateAccountConfirmationService = inject(
    Types.ValidateAccountConfirmationService
  );

  public isLoading = signal(true);
  public isSuccess = signal(false);
  public isError = signal(false);
  public errorMessage = signal('');

  private getCityIdentity(): string {
    const routeCity =
      this._route.parent?.snapshot.paramMap.get('city') ??
      this._route.snapshot.paramMap.get('city');

    if (routeCity) return routeCity;

    const handlerCity = this._appHandler.getCity();
    if (handlerCity?.identidade) return handlerCity.identidade;

    if (typeof window === 'undefined') return '';
    const segments = window.location.pathname.split('/').filter(Boolean);
    const firstSegment = segments[0] ?? '';
    const normalized = firstSegment.toLowerCase();

    if (normalized === 'accountconfirmation' || normalized === 'account-confirmation') {
      return '';
    }

    return firstSegment;
  }

  async ngOnInit(): Promise<void> {
    this._appHandler.setLoading(false);

    const token = this._route.snapshot.paramMap.get('token') ?? '';
    const userId = this._route.snapshot.paramMap.get('userId') ?? '';
    const cityIdentity = this.getCityIdentity();

    if (!token || !userId || !cityIdentity) {
      this.showError('Link inválido', 'Não foi possível identificar os dados do convite.');
      return;
    }

    try {
      const result = await this._validateAccountConfirmationService.handle({ token, userId });
      if (!result?.success) {
        throw new Error('Falha ao confirmar conta');
      }

      this.isSuccess.set(true);
      this.isLoading.set(false);

      // Delay redirect to allow user to see success message
      setTimeout(() => {
        this._router.navigate([`/${cityIdentity}`], {
          queryParams: { accountConfirmed: true },
        });
      }, 3000);
    } catch (error: unknown) {
      const errorMessage = this.extractErrorMessage(error);
      this.showError('Não foi possível confirmar sua conta', errorMessage);
    }
  }

  private extractErrorMessage(error: unknown): string {
    const defaultMessage = 'Tente novamente mais tarde. Se o problema persistir, entre em contato com o suporte.';

    if (!error) return defaultMessage;

    // Handle HttpErrorResponse from Angular
    if (typeof error === 'object' && error !== null) {
      const httpError = error as { error?: { message?: string }; message?: string };

      // Backend error message is usually in error.error.message
      if (httpError.error?.message) {
        return httpError.error.message;
      }

      // Fallback to error.message
      if (httpError.message) {
        return httpError.message;
      }
    }

    // Handle plain Error objects
    if (error instanceof Error) {
      return error.message || defaultMessage;
    }

    return defaultMessage;
  }

  private showError(title: string, message: string): void {
    this.isError.set(true);
    this.isLoading.set(false);
    this.errorMessage.set(message);
    
    // We still use appHandler for the actual error display if we want to keep the existing behavior,
    // or we can implement a local error UI. The plan says "Implement Loading and Success templates".
    // I'll keep the appHandler error for now but ensure local state is updated.
    this._appHandler.setError(true, {
      message: title,
      description: message,
      actionText: 'Voltar',
      action: () => {
        this._router.navigate(['/']);
      },
    });
  }
}
